/**
 * STORY-298: a chart answer drawn as an interactive chart.
 *
 * The answer's text keeps its block-glyph chart for every reader that gets
 * text (CLI, Telegram, A2A, the stream, the history); the web chat draws the
 * `chart_data` payload here instead: hover / arrow-key values, series toggles
 * in the legend, drag-to-zoom on the x range with a reset, and "Save as PNG"
 * for reports.
 *
 * Hand-drawn SVG, no charting dependency: the exported image is this SVG, so
 * what is saved is what is seen (utils/chartExport.ts adds the legend and a
 * white background, and drops the hover layer).
 *
 * Colours: eight categorical hues in fixed order (the dataviz reference
 * palette, light and dark steps); a ninth series and beyond reuse the hues
 * with a dash pattern, so identity is never colour alone. Bars carry no dash:
 * every bar is named on the x axis instead (slanted when the bars are narrow),
 * on screen and in the PNG. A colour belongs to
 * the series (its index in the payload), never to its rank among the visible
 * ones — hiding a series does not repaint the rest.
 */
import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Download, RotateCcw } from 'lucide-react';
import type { ChartPayload } from '@/types';
import { useTranslation, formatCount } from '@/i18n';
import { formatValue, niceMax } from '@/utils/chartPayload';
import { exportChartPng, standaloneChartSvg, type LegendEntry } from '@/utils/chartExport';
import { toast } from '@/stores/toastStore';

export const CHART_W = 720;
export const CHART_H = 360;
const M = { left: 56, right: 18, top: 70, bottom: 38 };
const PLOT_W = CHART_W - M.left - M.right;
// Narrow bars (band < 48, i.e. 14+ bars) name their host on a slanted label,
// which needs a taller bottom margin than a time axis.
const BAR_LABEL_MIN_BAND = 48;
const SLANT_BOTTOM = 96;
const SLANT_CHARS = 18;
// The tooltip lists at most this many series rows; the focused one is always among them.
export const TIP_MAX_ROWS = 12;

export const PALETTE_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'];
const PALETTE_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];
const DASHES: Array<string | undefined> = [undefined, '7 4', '2 3', '9 3 2 3'];

const seriesDash = (i: number) => DASHES[Math.floor(i / PALETTE_LIGHT.length) % DASHES.length];
const lightColor = (i: number) => PALETTE_LIGHT[i % PALETTE_LIGHT.length];

function fill(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce((s, [k, v]) => s.split(`{${k}}`).join(String(v)), template);
}

function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/**
 * Cut the middle, not the end: host names that share a prefix differ at the tail.
 * Every bar label (level or slanted) goes through this; the full name stays in
 * the label's <title> and in the hover tooltip.
 */
function clipMiddle(text: string, max: number): string {
  if (text.length <= max) return text;
  const tail = Math.floor((max - 1) / 2);
  return `${text.slice(0, max - 1 - tail)}…${text.slice(text.length - tail)}`;
}

interface Props {
  chart: ChartPayload;
}

interface Focus {
  index: number;
  series: number;
}

export function InteractiveChart({ chart }: Props) {
  const { t, language } = useTranslation();
  const tc = t.chart;
  const hintId = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const [hidden, setHidden] = useState<Set<number>>(() => new Set());
  const [zoom, setZoom] = useState<[number, number] | null>(null);
  const [focus, setFocus] = useState<Focus | null>(null);
  const [drag, setDrag] = useState<{ from: number; to: number } | null>(null);
  const [saving, setSaving] = useState(false);

  const dark = typeof document !== 'undefined'
    && document.documentElement.classList.contains('dark');
  const colorOf = useCallback(
    (i: number) => (dark ? PALETTE_DARK : PALETTE_LIGHT)[i % PALETTE_LIGHT.length],
    [dark],
  );

  const unit = chart.unit ?? '';
  const visible = useMemo(
    () => chart.series.map((_, i) => i).filter((i) => !hidden.has(i)),
    [chart.series, hidden],
  );

  // ── the x extent: points (line), visible bars (bar), buckets (histogram) ──
  const nPoints = chart.kind === 'line'
    ? Math.max(...chart.series.map((s) => s.values.length))
    : chart.kind === 'bar' ? visible.length : (chart.edges?.length ?? 1) - 1;
  const [i0, i1] = chart.kind === 'line' && zoom ? zoom : [0, Math.max(0, nPoints - 1)];

  // ── the y extent over what is visible ──
  const [yMin, yMax] = useMemo(() => {
    const vals: number[] = [];
    if (chart.kind === 'line') {
      for (const s of visible) {
        for (let i = i0; i <= i1; i += 1) {
          const v = chart.series[s].values[i];
          if (typeof v === 'number') vals.push(v);
        }
      }
    } else if (chart.kind === 'bar') {
      for (const s of visible) {
        const v = chart.series[s].values[0];
        if (typeof v === 'number') vals.push(v);
      }
    } else {
      for (const v of chart.series[0].values) if (typeof v === 'number') vals.push(v);
    }
    const lo = Math.min(0, ...vals);
    return [lo < 0 ? -niceMax(-lo) : 0, niceMax(Math.max(0, ...vals))];
  }, [chart, visible, i0, i1]);

  const band = PLOT_W / Math.max(1, nPoints);
  const slantBars = chart.kind === 'bar' && band < BAR_LABEL_MIN_BAND;
  const PLOT_H = CHART_H - M.top - (slantBars ? SLANT_BOTTOM : M.bottom);
  const yPos = (v: number) => M.top + PLOT_H - ((v - yMin) / (yMax - yMin || 1)) * PLOT_H;
  const xLine = (i: number) => (i1 === i0
    ? M.left + PLOT_W / 2
    : M.left + ((i - i0) / (i1 - i0)) * PLOT_W);
  const xBand = (k: number) => M.left + k * band;

  const spanMs = chart.x && chart.x.length > 1 ? chart.x[chart.x.length - 1] - chart.x[0] : 0;
  const xLabel = (i: number): string => {
    if (chart.x && typeof chart.x[i] === 'number') {
      return format(new Date(chart.x[i]), spanMs > 36 * 3600_000 ? 'dd.MM HH:mm' : 'HH:mm');
    }
    return fill(tc.point, { n: i + 1 });
  };
  const withUnit = (v: number) => `${formatValue(v)}${unit}`;

  // ── pointer → index ──
  const indexAt = (clientX: number): number => {
    const svg = svgRef.current;
    const rect = svg?.getBoundingClientRect();
    const vx = rect && rect.width > 0 ? ((clientX - rect.left) * CHART_W) / rect.width : clientX;
    if (chart.kind === 'line') {
      if (i1 === i0) return i0;
      const f = (vx - M.left) / PLOT_W;
      return Math.min(i1, Math.max(i0, Math.round(i0 + f * (i1 - i0))));
    }
    return Math.min(nPoints - 1, Math.max(0, Math.floor((vx - M.left) / band)));
  };

  const focusSeries = (s: number) => (visible.includes(s) ? s : visible[0]);

  const onMouseDown = (e: React.MouseEvent) => {
    if (chart.kind !== 'line') return;
    const i = indexAt(e.clientX);
    setDrag({ from: i, to: i });
  };
  const onMouseMove = (e: React.MouseEvent) => {
    const i = indexAt(e.clientX);
    setFocus((f) => ({ index: i, series: focusSeries(f?.series ?? visible[0]) }));
    if (drag) setDrag({ ...drag, to: i });
  };
  const onMouseUp = () => {
    if (drag && Math.abs(drag.to - drag.from) >= 1) {
      setZoom([Math.min(drag.from, drag.to), Math.max(drag.from, drag.to)]);
    }
    setDrag(null);
  };
  const onMouseLeave = () => {
    setDrag(null);
    if (document.activeElement !== svgRef.current) setFocus(null);
  };

  const lastIndex = chart.kind === 'line' ? i1 : nPoints - 1;
  const firstIndex = chart.kind === 'line' ? i0 : 0;
  const onKeyDown = (e: React.KeyboardEvent) => {
    const cur = focus ?? { index: firstIndex, series: visible[0] };
    let next: Focus | null = null;
    switch (e.key) {
      case 'ArrowRight': next = { ...cur, index: Math.min(lastIndex, cur.index + (focus ? 1 : 0)) }; break;
      case 'ArrowLeft': next = { ...cur, index: Math.max(firstIndex, cur.index - (focus ? 1 : 0)) }; break;
      case 'Home': next = { ...cur, index: firstIndex }; break;
      case 'End': next = { ...cur, index: lastIndex }; break;
      case 'ArrowUp':
      case 'ArrowDown': {
        if (chart.kind !== 'line') return;
        const at = visible.indexOf(focusSeries(cur.series));
        const step = e.key === 'ArrowDown' ? 1 : -1;
        next = { ...cur, series: visible[(at + step + visible.length) % visible.length] };
        break;
      }
      case '+':
      case '=': {
        if (chart.kind !== 'line' || i1 - i0 < 2) return;
        const half = Math.max(1, Math.floor((i1 - i0) / 4));
        const lo = Math.max(i0, cur.index - half);
        const hi = Math.min(i1, cur.index + half);
        setZoom([lo, Math.max(hi, lo + 1)]);
        next = cur;
        break;
      }
      case '0':
      case 'Escape':
        if (!zoom) return;
        setZoom(null);
        next = cur;
        break;
      default:
        return;
    }
    e.preventDefault();
    setFocus(next);
  };

  const toggle = (s: number) => {
    const isVisible = !hidden.has(s);
    if (isVisible && visible.length === 1) return; // the last visible series stays
    setHidden((prev) => {
      const nextSet = new Set(prev);
      if (isVisible) nextSet.add(s); else nextSet.delete(s);
      return nextSet;
    });
  };

  // Zoomed in, the title and the period still name the whole answer; the note
  // says which part is on screen, so a saved zoom does not contradict itself.
  const zoomNote = chart.kind === 'line' && zoom
    ? fill(chart.period ? tc.zoomedOf : tc.zoomed, { from: xLabel(i0), to: xLabel(i1), period: chart.period ?? '' })
    : '';
  const notes = [
    zoomNote || (chart.period ? fill(tc.period, { period: chart.period }) : ''),
    chart.truncated ? fill(tc.truncated, chart.truncated) : '',
    chart.downsampled ? fill(tc.downsampled, chart.downsampled) : '',
  ].filter(Boolean).join(' · ');

  const showLegend = chart.kind !== 'histogram';

  const save = async () => {
    const svg = svgRef.current;
    if (!svg || saving) return;
    setSaving(true);
    try {
      const legend: LegendEntry[] = showLegend
        ? visible.map((s) => ({ label: chart.series[s].label, color: lightColor(s), dash: seriesDash(s) }))
        : [];
      const how = await exportChartPng(standaloneChartSvg(svg, legend));
      if (how === 'svg') toast.warning(tc.savedSvg);
    } catch {
      toast.error(tc.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  // ── tooltip text (also read out by the live region) ──
  let tipHead = '';
  const tipRows: Array<{ s: number; text: string; strong: boolean }> = [];
  if (focus) {
    if (chart.kind === 'line') {
      tipHead = xLabel(focus.index);
      for (const s of visible) {
        const v = chart.series[s].values[focus.index];
        tipRows.push({
          s,
          text: `${chart.series[s].label}: ${typeof v === 'number' ? withUnit(v) : tc.noValue}`,
          strong: s === focusSeries(focus.series),
        });
      }
    } else if (chart.kind === 'bar') {
      const s = visible[focus.index];
      if (s !== undefined) {
        const v = chart.series[s].values[0];
        tipHead = chart.series[s].label;
        tipRows.push({ s, text: typeof v === 'number' ? withUnit(v) : tc.noValue, strong: true });
      }
    } else if (chart.edges) {
      const e = chart.edges;
      tipHead = `${tc.bucket} ${formatValue(e[focus.index])}–${formatValue(e[focus.index + 1])}${unit}`;
      const count = chart.series[0].values[focus.index] ?? 0;
      tipRows.push({ s: 0, text: formatCount(language, count, tc.countUnits[chart.count_unit ?? 'points']), strong: true });
    }
  }
  const tipX = !focus ? 0 : chart.kind === 'line' ? xLine(focus.index) : xBand(focus.index) + band / 2;
  const liveText = focus ? [tipHead, ...tipRows.map((r) => r.text)].join('; ') : '';
  // Past TIP_MAX_ROWS the tooltip keeps the first rows in payload order, always
  // including the focused series (it replaces the last slot when it falls
  // outside), and says how many rows it left out. The live region reads all.
  // (`>` vs `>=` here is an equivalent mutant: at exactly TIP_MAX_ROWS rows the
  // focused row is always within the first TIP_MAX_ROWS and nothing is cut.)
  let tipShown = tipRows;
  if (tipRows.length > TIP_MAX_ROWS) {
    const strongAt = tipRows.findIndex((r) => r.strong);
    tipShown = strongAt < TIP_MAX_ROWS
      ? tipRows.slice(0, TIP_MAX_ROWS)
      : [...tipRows.slice(0, TIP_MAX_ROWS - 1), tipRows[strongAt]];
  }
  // (Equivalent mutant: `tipRows.length - TIP_MAX_ROWS` — a cut tooltip always
  // shows exactly TIP_MAX_ROWS rows. Counting what is shown keeps it honest if
  // that ever changes.)
  const tipHiddenCount = tipRows.length - tipShown.length;

  // ── marks ──
  const marks: React.ReactNode[] = [];
  if (chart.kind === 'line') {
    for (const s of visible) {
      const vals = chart.series[s].values;
      let d = '';
      let open = false;
      const dots: number[] = [];
      for (let i = i0; i <= i1; i += 1) {
        const v = vals[i];
        if (typeof v !== 'number') { open = false; continue; }
        const lone = typeof vals[i - 1] !== 'number' && typeof vals[i + 1] !== 'number';
        if (lone) dots.push(i);
        d += `${open ? 'L' : 'M'}${xLine(i).toFixed(1)},${yPos(v).toFixed(1)}`;
        open = true;
      }
      marks.push(
        <path key={`l${s}`} data-series={s} d={d} fill="none" stroke={colorOf(s)}
          data-export-color={lightColor(s)} strokeWidth={2} strokeLinejoin="round"
          strokeLinecap="round" strokeDasharray={seriesDash(s)} />,
      );
      for (const i of dots) {
        marks.push(
          <circle key={`d${s}-${i}`} cx={xLine(i)} cy={yPos(vals[i] as number)} r={2.5}
            fill={colorOf(s)} data-export-color={lightColor(s)} />,
        );
      }
    }
  } else if (chart.kind === 'bar') {
    visible.forEach((s, k) => {
      const v = chart.series[s].values[0];
      if (typeof v !== 'number') return;
      const w = Math.max(2, band * 0.7);
      const y = yPos(Math.max(v, 0));
      marks.push(
        <rect key={`b${s}`} data-series={s} x={xBand(k) + (band - w) / 2} y={y} width={w}
          height={Math.max(0, yPos(Math.min(v, 0)) - y)} rx={2} fill={colorOf(s)}
          data-export-color={lightColor(s)} />,
      );
    });
  } else {
    chart.series[0].values.forEach((v, k) => {
      const count = typeof v === 'number' ? v : 0;
      const y = yPos(count);
      marks.push(
        <rect key={`h${k}`} data-bucket={k} x={xBand(k) + 1} y={y} width={Math.max(1, band - 2)}
          height={Math.max(0, M.top + PLOT_H - y)} rx={2} fill={colorOf(0)}
          data-export-color={lightColor(0)} />,
      );
    });
  }

  // ── axes ──
  const yTicks = [0, 1, 2, 3, 4].map((k) => yMin + ((yMax - yMin) * k) / 4);
  const xTicks: Array<{ x: number; label: string; full?: string }> = [];
  if (chart.kind === 'line') {
    const count = Math.min(5, i1 - i0 + 1);
    for (let k = 0; k < count; k += 1) {
      const i = count === 1 ? i0 : Math.round(i0 + ((i1 - i0) * k) / (count - 1));
      xTicks.push({ x: xLine(i), label: xLabel(i) });
    }
  } else if (chart.kind === 'histogram' && chart.edges) {
    const every = Math.ceil(chart.edges.length / 8);
    chart.edges.forEach((e, k) => {
      if (k % every === 0 || k === chart.edges!.length - 1) xTicks.push({ x: xBand(k), label: formatValue(e) });
    });
  } else if (!slantBars) {
    visible.forEach((s, k) => xTicks.push({
      x: xBand(k) + band / 2,
      label: clipMiddle(chart.series[s].label, Math.floor(band / 7)),
      full: chart.series[s].label,
    }));
  }
  const slantLabels = slantBars
    ? visible.map((s, k) => ({ x: xBand(k) + band / 2, label: clipMiddle(chart.series[s].label, SLANT_CHARS), full: chart.series[s].label }))
    : [];
  const yUnit = chart.kind === 'histogram' ? '' : unit;

  return (
    <div
      data-interactive-chart
      className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3"
    >
      <div className="flex flex-wrap items-center justify-end gap-2 mb-1">
        {zoom && (
          <button
            type="button"
            onClick={() => setZoom(null)}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
            {tc.resetZoom}
          </button>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <Download className="w-3.5 h-3.5" aria-hidden="true" />
          {saving ? tc.saving : tc.savePng}
        </button>
      </div>

      <div className="relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          className="w-full h-auto text-gray-700 dark:text-gray-200 select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
          role="group"
          aria-label={`${tc.region}: ${chart.title}`}
          aria-describedby={hintId}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onFocus={() => setFocus((f) => f ?? { index: firstIndex, series: visible[0] })}
          onBlur={() => setFocus(null)}
          fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif"
        >
          <text x={16} y={26} fontSize={15} fontWeight={600} fill="currentColor">
            {clip(chart.title, 90)}
          </text>
          {notes && (
            <text data-chart-notes x={16} y={48} fontSize={12} fill="currentColor" opacity={0.75}>
              {notes}
            </text>
          )}

          {yTicks.map((v) => (
            <g key={`y${v}`}>
              <line x1={M.left} x2={M.left + PLOT_W} y1={yPos(v)} y2={yPos(v)}
                stroke="currentColor" strokeOpacity={0.12} />
              <text x={M.left - 6} y={yPos(v) + 4} fontSize={11} textAnchor="end"
                fill="currentColor" opacity={0.7}>
                {`${formatValue(Math.round(v * 100) / 100)}${yUnit}`}
              </text>
            </g>
          ))}
          <line x1={M.left} x2={M.left + PLOT_W} y1={M.top + PLOT_H} y2={M.top + PLOT_H}
            stroke="currentColor" strokeOpacity={0.35} />
          {xTicks.map((tick, k) => (
            <g key={`x${k}`}>
              {tick.full && tick.full !== tick.label && <title>{tick.full}</title>}
              <text x={tick.x} y={M.top + PLOT_H + 18} fontSize={11}
                textAnchor="middle" fill="currentColor" opacity={0.7}>
                {tick.label}
              </text>
            </g>
          ))}
          {slantLabels.map((tick, k) => {
            const y = M.top + PLOT_H + 12;
            return (
              <g key={`xs${k}`}>
                {tick.full !== tick.label && <title>{tick.full}</title>}
                <text data-bar-label x={tick.x} y={y} fontSize={10} textAnchor="end"
                  transform={`rotate(-40 ${tick.x.toFixed(1)} ${y})`} fill="currentColor" opacity={0.8}>
                  {tick.label}
                </text>
              </g>
            );
          })}

          <g data-marks>{marks}</g>

          {drag && drag.to !== drag.from && (
            <rect data-export-skip data-zoom-selection
              x={xLine(Math.min(drag.from, drag.to))} y={M.top}
              width={Math.abs(xLine(drag.to) - xLine(drag.from))} height={PLOT_H}
              fill="#2a78d6" fillOpacity={0.12} />
          )}
          {focus && (
            <line data-export-skip data-crosshair x1={tipX} x2={tipX} y1={M.top} y2={M.top + PLOT_H}
              stroke="currentColor" strokeOpacity={0.4} strokeDasharray="3 3" />
          )}
          {focus && chart.kind === 'line' && tipRows.map((r) => {
            const v = chart.series[r.s].values[focus.index];
            return typeof v === 'number' ? (
              <circle key={`f${r.s}`} data-export-skip cx={tipX} cy={yPos(v)} r={r.strong ? 5 : 3.5}
                fill={colorOf(r.s)} stroke="white" strokeWidth={2} />
            ) : null;
          })}

          <rect
            data-export-skip
            data-plot-overlay
            x={M.left}
            y={M.top}
            width={PLOT_W}
            height={PLOT_H}
            fill="transparent"
            style={{ cursor: chart.kind === 'line' ? 'crosshair' : 'default' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
          />
        </svg>

        {focus && tipHead && (
          <div
            data-chart-tooltip
            className="pointer-events-none absolute z-10 rounded-md border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 px-2 py-1 text-xs text-gray-800 dark:text-gray-100 shadow"
            style={{
              top: `${(M.top / CHART_H) * 100}%`,
              left: `${(tipX / CHART_W) * 100}%`,
              transform: tipX > CHART_W * 0.6 ? 'translateX(calc(-100% - 8px))' : 'translateX(8px)',
            }}
          >
            <div className="font-medium">{tipHead}</div>
            {tipShown.map((r) => (
              <div key={r.s} className={r.strong ? 'font-semibold' : undefined}>
                <span aria-hidden="true" className="inline-block w-2 h-2 mr-1 rounded-full align-middle"
                  style={{ backgroundColor: colorOf(r.s) }} />
                {r.text}
              </div>
            ))}
            {tipHiddenCount > 0 && <div data-tip-more>{fill(tc.moreSeries, { n: tipHiddenCount })}</div>}
          </div>
        )}
      </div>

      <p id={hintId} className="sr-only">{tc.plotHint}</p>
      <div aria-live="polite" className="sr-only" data-chart-live>{liveText}</div>

      {showLegend && (
        <div role="group" aria-label={tc.legend} className="mt-2 flex flex-wrap gap-1.5">
          {chart.series.map((s, i) => {
            const on = !hidden.has(i);
            const locked = on && visible.length === 1;
            return (
              <button
                key={i}
                type="button"
                data-legend-series={i}
                aria-pressed={on}
                aria-disabled={locked || undefined}
                aria-label={fill(tc.toggleSeries, { label: s.label })}
                title={locked ? tc.lastSeries : s.label}
                onClick={() => toggle(i)}
                className={`inline-flex max-w-[16rem] items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  on
                    ? 'border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100'
                    : 'border-dashed border-gray-300 dark:border-gray-700 text-gray-400 dark:text-gray-500 line-through'
                }`}
              >
                <svg width="18" height="8" aria-hidden="true" className="shrink-0">
                  <line x1="0" x2="18" y1="4" y2="4" stroke={on ? colorOf(i) : '#9ca3af'}
                    strokeWidth="3" strokeDasharray={seriesDash(i)} />
                </svg>
                <span className="truncate">{s.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* a truncated chart keeps its text block in the message (MessageItem) */}
      {chart.truncated && (
        <p data-chart-full-list className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {fill(tc.fullListInText, chart.truncated)}
        </p>
      )}
    </div>
  );
}
